import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePendingInvitations } from '../hooks/usePendingInvitations';
import { Users, X, ChevronRight, Mail } from 'lucide-react';

function PendingInvitationsBanner({ userEmail }) {
  const { invitations, loading, hasPendingInvitations } = usePendingInvitations(userEmail);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  // Don't show if loading, no invitations, or dismissed
  if (loading || !hasPendingInvitations || dismissed) {
    return null;
  }

  const handleViewInvitation = (teamId, inviteId) => {
    navigate(`/team/invite?teamId=${teamId}&inviteId=${inviteId}`);
  };

  // Simple banner for single invitation
  if (invitations.length === 1) {
    const invite = invitations[0];
    return (
      <div
        className="invitation-banner-protected"
        style={{
          background: 'linear-gradient(to right, #3B82F6, #2563EB)',
          color: 'white'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 flex-shrink-0" style={{ color: 'white' }} />
              <p className="text-sm font-medium" style={{ color: 'white', margin: 0 }}>
                You have an invitation to join <strong style={{ color: 'white' }}>{invite.teamName}</strong>
              </p>
              <button
                onClick={() => handleViewInvitation(invite.teamId, invite.inviteId)}
                className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '0.375rem',
                  padding: '0.25rem 0.75rem'
                }}
              >
                View Invitation
                <ChevronRight className="h-4 w-4" style={{ color: 'white' }} />
              </button>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 rounded-md"
              style={{
                color: 'white',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expandable banner for multiple invitations
  return (
    <div
      className="invitation-banner-protected"
      style={{
        background: 'linear-gradient(to right, #3B82F6, #2563EB)',
        color: 'white'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 flex-shrink-0" style={{ color: 'white' }} />
              <p className="text-sm font-medium" style={{ color: 'white', margin: 0 }}>
                You have <strong style={{ color: 'white' }}>{invitations.length} pending team invitations</strong>
              </p>
              <button
                onClick={() => setExpanded(!expanded)}
                className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '0.375rem',
                  padding: '0.25rem 0.75rem'
                }}
              >
                {expanded ? 'Hide' : 'Show All'}
                <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} style={{ color: 'white' }} />
              </button>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 rounded-md"
              style={{
                color: 'white',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {expanded && (
            <div className="mt-3 space-y-2 pb-2">
              {invitations.map((invite) => (
                <div
                  key={`${invite.teamId}-${invite.inviteId}`}
                  className="flex items-center justify-between bg-white/10 backdrop-blur rounded-lg px-4 py-2"
                >
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4" />
                    <div>
                      <p className="font-medium">{invite.teamName}</p>
                      <p className="text-xs opacity-90">
                        Expires {new Date(invite.expiresAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewInvitation(invite.teamId, invite.inviteId)}
                    className="px-3 py-1 bg-white text-blue-600 hover:bg-blue-50 rounded-md text-sm font-medium transition-colors"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PendingInvitationsBanner;